/**
 * resolveNotificationCopy — the ONE rendering chain for a notification card.
 *
 * ─── Why this is shared rather than written per app ──────────────────────────
 *
 * The three portals rendered notifications three different ways (H13), from the
 * same API response:
 *
 *   • journey-web    `t(k, n.title)`  — masked a missing key with the server's
 *                                        stored English, so non-EN users read
 *                                        English and nobody noticed. Removed in
 *                                        W0.4, which moved it to `t(k)`.
 *   • business-web   `t(k)`           — renders the RAW KEY at the user when the
 *                                        key is missing (`fallbackLng: false`).
 *   • citizen-mobile `(k ? t(k,'') : '') || n.title` — correct.
 *
 * The correct one was the phone's, so the other two moved to it. Having three
 * copies of a four-line chain is what let them diverge in the first place, so
 * there is now one.
 *
 * ─── Why COLUMN-first, key-second (N15, W7) ──────────────────────────────────
 *
 * This chain used to prefer the key. The argument for that was language
 * switching: the column is rendered once, in the locale the recipient had at
 * write time, so a user who later changes language would read stale-locale
 * text. Real, but it is the smaller of the two failures, and it costs the
 * larger one.
 *
 * The server renders `title`/`body` from the SAME key catalogue, in the
 * recipient's locale, WITH the notification's own interpolation values, at
 * write time (W4.6, DW4-5). The client branch can only interpolate from
 * `data.values` — and no emitter writes `values` today, so for a key that
 * carries a `{{placeholder}}` the key branch renders the placeholder literally
 * at the user while the column right beside it holds the real name. Preferring
 * the key means preferring the copy that CANNOT see the data.
 *
 * So: column first, key second. Neither branch may ever be deleted.
 *
 *   • The COLUMN is the normal path, and the only one that carries data.
 *   • The KEY is the legacy-row path: rows written before migration 0127's
 *     backfill have an empty column and nothing BUT a key.
 *
 * Switching language is served by the server re-rendering on the next write,
 * not by the client re-rendering an old row from a placeholder-shaped key.
 *
 * ─── Why `t(key, '')` and not `t(key, column)` ───────────────────────────────
 *
 * The `''` is CONTROL FLOW — "did the key resolve?" — not English copy, and it
 * is the one form root CLAUDE.md sanctions. A text fallback passed to `t()` is
 * forbidden outright because it renders that text whenever the key is missing
 * OR merely untranslated, so the screen looks fine while the gap goes unnoticed.
 * Here a missing key resolves to `''` and the caller renders nothing, rather
 * than English at a non-EN reader. (Since N15 this branch is reached only when
 * there is no column at all.)
 */

/**
 * The i18next overload this chain uses: `t(key, defaultValue, options?)`.
 *
 * `defaultValue` is REQUIRED, not optional — that is i18next's own signature for
 * the three-argument form, and typing it as optional made the real `TFunction`
 * unassignable to this parameter in every caller.
 */
type TFn = (key: string, defaultValue: string, options?: Record<string, unknown>) => string;

/** The `data` blob a notification carries: i18n keys + per-type payload. */
export interface NotificationCopySource {
  /** `data.titleKey` / `data.bodyKey` — absent for data-authored copy. */
  key?: string | null;
  /** `data.values` — interpolation for `{{placeholders}}` in the key. */
  values?: Record<string, string | number> | null;
  /** The server-rendered column (`title` / `body`). */
  column?: string | null;
}

/**
 * Resolve one notification field (title or body) to display text.
 *
 * Returns `''` when there is neither a resolvable key nor a column — the caller
 * decides what an empty body looks like, which is not this function's business.
 */
export function resolveNotificationCopy(t: TFn, source: NotificationCopySource): string {
  const { key, values, column } = source;
  if (column) return column;
  // Evaluated only when there is no column, so `t()` is not called — and cannot
  // warn about a missing key — on the path that never uses its result.
  return (key ? t(key, '', values ? { ...values } : undefined) : '') || '';
}

/**
 * Read `titleKey`/`bodyKey`/`values` out of a notification's untyped `data`
 * blob. The blob is `Record<string, unknown>` on every client, so each one was
 * writing the same three casts inline.
 */
export function notificationCopyKeys(data: Record<string, unknown> | null | undefined): {
  titleKey?: string;
  bodyKey?: string;
  values?: Record<string, string | number>;
} {
  const titleKey =
    typeof data?.['titleKey'] === 'string' ? (data['titleKey'] as string) : undefined;
  const bodyKey = typeof data?.['bodyKey'] === 'string' ? (data['bodyKey'] as string) : undefined;
  const raw = data?.['values'];
  const values =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, string | number>)
      : undefined;
  return { titleKey, bodyKey, values };
}
